import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid2,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import RichTextEditor from "../../../../../views/components/common/textEditor/RichTextEditor";
import { SwayyProductDetailStore } from "../../../stores/SwayyProductDetailStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";

interface SwayyProductInfoFormProps {
  store: SwayyProductDetailStore;
  mode?: "create" | "edit";
  disabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
}

const selectedOptions = (options: FilterValue[], ids: string[]) =>
  options.filter((option) => ids.includes(option.id));

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: swayyRadii.sm,
    backgroundColor: swayyColors.white,
    "& fieldset": { borderColor: swayyColors.border },
    "&:hover fieldset": { borderColor: swayyColors.pine },
    "&.Mui-focused fieldset": { borderColor: swayyColors.pine },
  },
  "& input, & textarea": {
    fontSize: 14,
    color: swayyColors.deepGreen,
  },
};

const FieldError = ({ message }: { message?: string }) => {
  if (!message) return null;
  return (
    <CustomTypography
      variant="caption"
      color={swayyColors.danger}
      sx={{ mt: 0.5, display: "block" }}
    >
      {message}
    </CustomTypography>
  );
};

const FieldLabel = ({
  label,
  hint,
  required = false,
}: {
  label: string;
  hint?: string;
  required?: boolean;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: 0.75, minHeight: 18 }}
  >
    <CustomTypography
      variant="caption"
      color={swayyColors.textSecondary}
      sx={{ fontWeight: 600, lineHeight: 1.2 }}
    >
      {label}
      {required && (
        <Box
          component="span"
          sx={{
            color: swayyColors.danger,
            ml: 0.5,
            fontSize: "14px",
            lineHeight: 1,
            verticalAlign: "middle",
          }}
        >
          *
        </Box>
      )}
    </CustomTypography>
    {hint && (
      <CustomTypography variant="caption" color={swayyColors.textSecondary}>
        {hint}
      </CustomTypography>
    )}
  </Stack>
);

const SectionTitle = ({ title }: { title: string }) => (
  <CustomTypography
    variant="body2"
    color={swayyColors.deepGreen}
    sx={{ fontWeight: 700, mb: 1.5 }}
  >
    {title}
  </CustomTypography>
);

const SwayyProductInfoForm = ({
  store,
  mode = "edit",
  disabled = false,
  onCancel,
  onSave,
}: SwayyProductInfoFormProps) => {
  const { form } = store;
  const isCreate = mode === "create";

  const dropdownProps = {
    local: true as const,
    isMultiSelect: false as const,
    accentColor: swayyColors.pine,
    backgroundColor: swayyColors.white,
    borderColor: swayyColors.border,
    borderRadius: swayyRadii.sm,
    inputHeight: "40px",
    disabled: disabled || store.isLoadingOptions,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
          gap: 2.5,
        }}
      >
        {/* Left: main content (60%) */}
        <Box sx={{ width: { xs: "100%", md: "60%" }, minWidth: 0 }}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: swayyRadii.lg,
              borderColor: swayyColors.border,
              backgroundColor: swayyColors.white,
            }}
          >
            <SectionTitle title="Product content" />
            <Stack spacing={2}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FieldLabel label="Name" required />
                  <TextField
                    fullWidth
                    size="small"
                    value={form.name}
                    disabled={disabled}
                    error={Boolean(store.fieldErrors.name)}
                    helperText={store.fieldErrors.name}
                    onChange={(event) =>
                      store.updateForm("name", event.target.value)
                    }
                    sx={fieldSx}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FieldLabel label="Slug" />
                  <TextField
                    fullWidth
                    size="small"
                    value={form.slug}
                    disabled={disabled}
                    onChange={(event) =>
                      store.updateForm("slug", event.target.value)
                    }
                    sx={fieldSx}
                  />
                </Grid2>
              </Grid2>

              <Box>
                <FieldLabel label="Description" />
              <Box
                sx={{
                  "& > .MuiBox-root": {
                    borderColor: `${swayyColors.border} !important`,
                    borderRadius: `${swayyRadii.sm} !important`,
                    backgroundColor: swayyColors.white,
                    overflow: "visible !important",
                    flexDirection: "column",
                  },
                  "& .ql-toolbar.ql-snow": {
                    border: "none",
                    borderBottom: `1px solid ${swayyColors.border}`,
                    boxShadow: "none !important",
                    padding: "8px",
                    minHeight: "unset",
                    lineHeight: 1,
                    overflow: "visible",
                  },
                  "& .ql-toolbar.ql-snow .ql-formats": {
                    marginRight: "8px",
                  },
                  "& .ql-toolbar.ql-snow .ql-picker": {
                    overflow: "visible",
                  },
                  "& .ql-toolbar.ql-snow .ql-picker-options": {
                    zIndex: 20,
                  },
                  "& .ql-container.ql-snow": {
                    border: "none",
                  },
                  "& .ql-editor": {
                    padding: "8px 12px",
                  },
                }}
              >
                <RichTextEditor
                  value={form.description}
                  onChange={(value) => store.updateForm("description", value)}
                  placeholder="Write product description..."
                  maxLength={1000}
                  disabled={disabled}
                  minHeight="120px"
                />
              </Box>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Right: meta sidebar (40%), sticky on desktop */}
        <Box
          sx={{
            width: { xs: "100%", md: "40%" },
            minWidth: 0,
            position: { md: "sticky" },
            top: { md: 72 },
          }}
        >
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: swayyRadii.lg,
              borderColor: swayyColors.border,
              backgroundColor: swayyColors.white,
            }}
          >
            <SectionTitle title="Classification" />
            <Grid2 container spacing={2} alignItems="flex-start">
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FieldLabel label="Primary category" required />
                <AppSearchDropdownMulti
                  {...dropdownProps}
                  title="Select category"
                  list={store.categoryOptions}
                  value={selectedOptions(store.categoryOptions, [
                    String(form.primaryCategoryId ?? ""),
                  ])}
                  handleSelect={(items) => {
                    const primaryId = items[0]?.id
                      ? Number(items[0].id)
                      : null;
                    store.updateForm("primaryCategoryId", primaryId);
                    if (primaryId) {
                      store.updateForm(
                        "additionalCategoryIds",
                        form.additionalCategoryIds.filter(
                          (id) => id !== primaryId,
                        ),
                      );
                    }
                  }}
                />
                <FieldError message={store.fieldErrors.primaryCategoryId} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FieldLabel label="Additional categories" />
                <AppSearchDropdownMulti
                  {...dropdownProps}
                  isMultiSelect
                  title="Select additional categories"
                  list={store.categoryOptions.filter(
                    (option) =>
                      option.id !== String(form.primaryCategoryId ?? ""),
                  )}
                  value={selectedOptions(
                    store.categoryOptions,
                    form.additionalCategoryIds.map(String),
                  )}
                  handleSelect={(items) =>
                    store.updateForm(
                      "additionalCategoryIds",
                      items
                        .map((item) => Number(item.id))
                        .filter(
                          (id) =>
                            Boolean(id) && id !== form.primaryCategoryId,
                        ),
                    )
                  }
                />
              </Grid2>
            </Grid2>

            <Box sx={{ mt: 2.5 }}>
              <SectionTitle title="Tax & channels" />
            </Box>
            <Stack spacing={2}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FieldLabel label="HSN code" />
                  <TextField
                    fullWidth
                    size="small"
                    value={form.hsnCode}
                    disabled={disabled}
                    error={Boolean(store.fieldErrors.hsnCode)}
                    helperText={store.fieldErrors.hsnCode}
                    onChange={(event) =>
                      store.updateForm("hsnCode", event.target.value)
                    }
                    sx={fieldSx}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FieldLabel label="Tax %" />
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={form.taxRate ?? ""}
                    disabled={disabled}
                    error={Boolean(store.fieldErrors.taxRate)}
                    helperText={store.fieldErrors.taxRate}
                    onChange={(event) => {
                      const raw = event.target.value;
                      store.updateForm(
                        "taxRate",
                        raw === "" ? null : Number(raw),
                      );
                    }}
                    sx={fieldSx}
                  />
                </Grid2>
              </Grid2>

              <Box>
                <FieldLabel label="Sales channels" />
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ minHeight: 40, alignItems: "center" }}
                >
                  {(
                    [
                      { key: "isB2c" as const, label: "B2C" },
                      { key: "isB2b" as const, label: "B2B" },
                    ] as const
                  ).map((channel) => {
                    const checked = form[channel.key];
                    return (
                      <FormControlLabel
                        key={channel.key}
                        sx={{
                          m: 0,
                          px: 1.25,
                          py: 0.5,
                          minHeight: 40,
                          borderRadius: swayyRadii.full,
                          border: `1px solid ${
                            checked ? swayyColors.pine : swayyColors.border
                          }`,
                          backgroundColor: checked
                            ? swayyColors.pineLight
                            : swayyColors.white,
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={disabled}
                            onChange={(event) =>
                              store.updateForm(
                                channel.key,
                                event.target.checked,
                              )
                            }
                            sx={{
                              p: 0.25,
                              mr: 0.5,
                              color: swayyColors.border,
                              "&.Mui-checked": { color: swayyColors.pine },
                            }}
                          />
                        }
                        label={
                          <CustomTypography
                            variant="body2"
                            color={swayyColors.deepGreen}
                            sx={{ fontWeight: 600 }}
                          >
                            {channel.label}
                          </CustomTypography>
                        }
                      />
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          mt: 2.5,
          p: { xs: 2, md: 2.5 },
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          backgroundColor: swayyColors.white,
        }}
      >
        <SectionTitle title="Attributes" />
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FieldLabel label="Brand" />
            <AppSearchDropdownMulti
              {...dropdownProps}
              title="Select brand"
              list={store.brandOptions}
              value={selectedOptions(store.brandOptions, [
                String(form.brandId ?? ""),
              ])}
              handleSelect={(items) =>
                store.updateForm(
                  "brandId",
                  items[0]?.id ? Number(items[0].id) : null,
                )
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FieldLabel label="Priority" />
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.priority}
              disabled={disabled}
              error={Boolean(store.fieldErrors.priority)}
              helperText={store.fieldErrors.priority}
              onChange={(event) =>
                store.updateForm("priority", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <FieldLabel label="Length" />
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.length}
              disabled={disabled}
              error={Boolean(store.fieldErrors.length)}
              helperText={store.fieldErrors.length}
              onChange={(event) =>
                store.updateForm("length", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <FieldLabel label="Breadth" />
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.breadth}
              disabled={disabled}
              error={Boolean(store.fieldErrors.breadth)}
              helperText={store.fieldErrors.breadth}
              onChange={(event) =>
                store.updateForm("breadth", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <FieldLabel label="Height" />
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.height}
              disabled={disabled}
              error={Boolean(store.fieldErrors.height)}
              helperText={store.fieldErrors.height}
              onChange={(event) =>
                store.updateForm("height", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }}>
            <FieldLabel label="Weight" />
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.weight}
              disabled={disabled}
              error={Boolean(store.fieldErrors.weight)}
              helperText={store.fieldErrors.weight}
              onChange={(event) =>
                store.updateForm("weight", event.target.value)
              }
              sx={fieldSx}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={1.5}
        justifyContent="flex-end"
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${swayyColors.border}`,
        }}
      >
        <AppButton
          label="Cancel"
          onClick={onCancel}
          disabled={store.isSaving}
          customStyles={{
            width: { xs: "100%", sm: "auto" },
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            borderRadius: swayyRadii.md,
            "&:hover": { backgroundColor: swayyColors.pineLight },
          }}
        />
        <AppButton
          label={
            store.isSaving
              ? isCreate
                ? "Creating..."
                : "Saving..."
              : isCreate
                ? "Create product"
                : "Save changes"
          }
          onClick={onSave}
          disabled={
            disabled ||
            store.isSaving ||
            (isCreate ? !store.canCreate : !store.hasChanges)
          }
          customStyles={{
            width: { xs: "100%", sm: "auto" },
            color: swayyColors.white,
            backgroundColor: swayyColors.pine,
            borderRadius: swayyRadii.md,
            "&:hover": { backgroundColor: swayyColors.pineHover },
            "&.Mui-disabled": {
              color: swayyColors.white,
              backgroundColor: swayyColors.pine,
              opacity: 0.5,
            },
          }}
        />
      </Stack>
    </Box>
  );
};

export default observer(SwayyProductInfoForm);
